import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  useTheme,
  Chip,
  Stack,
  Avatar,
  Button,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  EnergySavingsLeaf,
  WaterDrop,
  Recycling,
  Public,
  TrendingDown,
  CheckCircle,
  ArrowForward,
  Forest,
  School,
  Business,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import CTASection from '../components/common/CTASection';

const Sustainability = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  // Updated stats to match home page data
  const achievements = [
    { number: '500,000+', label: 'KM of Fiber Network', icon: <Public />, color: '#e60108' },
    { number: '10,000+', label: 'Telecom Towers', icon: <TrendingDown />, color: '#191919' },
    { number: '150+', label: 'Countries Served', icon: <Forest />, color: '#e60108' },
    { number: '100%', label: 'Uptime Guarantee', icon: <EnergySavingsLeaf />, color: '#191919' },
  ];

  const initiatives = [
    {
      icon: <EnergySavingsLeaf />,
      title: 'Renewable Energy',
      description: 'Powering our operations with 50% renewable energy by 2025',
      progress: 65,
      target: '50% by 2025',
      color: '#e60108',
    },
    {
      icon: <TrendingDown />,
      title: 'Carbon Reduction',
      description: 'Reducing carbon emissions by 40% across all operations',
      progress: 45,
      target: '40% by 2026',
      color: '#191919',
    },
    {
      icon: <WaterDrop />,
      title: 'Water Conservation',
      description: 'Implementing water-efficient cooling in data centers',
      progress: 70,
      target: '30% reduction by 2025',
      color: '#e60108',
    },
    {
      icon: <Recycling />,
      title: 'Zero E-Waste',
      description: 'Responsible recycling of all electronic equipment',
      progress: 85,
      target: '100% by 2026',
      color: '#a0a0a0',
    },
  ];

  const greenTech = [
    'Liquid cooling systems reducing energy consumption by 40%',
    'AI-powered energy optimization in data centers',
    'Solar panel installations at tower sites',
    'Electric vehicle fleet for field operations',
    'Paperless office initiative - 95% reduction in paper use',
    'Heat recycling systems for building heating',
  ];

  return (
    <Box>
      {/* Hero Section with Image */}
      <Box
        sx={{
          position: 'relative',
          height: '50vh',
          minHeight: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src="/images/sustainability.jpg"
          alt="Sustainability"
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
          }}
        />
        
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.5) 100%)',
            zIndex: 1,
          }}
        />
        
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Chip
              label="Sustainability"
              icon={<Business sx={{ fontSize: 16 }} />}
              sx={{
                bgcolor: '#e60108',
                color: '#fefefe',
                mb: 3,
                fontWeight: 700,
                letterSpacing: '0.5px',
                mx: 'auto',
                '& .MuiChip-icon': { color: '#fefefe' },
              }}
            />
            <Typography
              variant="h1"
              sx={{
                color: '#fefefe',
                mb: 2,
                fontSize: { xs: '2.5rem', md: '4rem' },
                fontWeight: 800,
                lineHeight: 1.2,
                textShadow: '0 2px 20px rgba(0,0,0,0.3)',
              }}
            >
              Sustainability
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'rgba(254,254,254,0.9)',
                maxWidth: 700,
                mx: 'auto',
                fontSize: '1.25rem',
                lineHeight: 1.6,
              }}
            >
              Connecting the world responsibly with eco-friendly infrastructure across 150+ countries
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Stats Section */}
      <Box sx={{ py: 6, bgcolor: '#fefefe' }}>
        <Container maxWidth="xl">
          <Grid container spacing={3} justifyContent="center">
            {achievements.map((achievement, index) => (
              <Grid item xs={6} sm={3} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  style={{ textAlign: 'center' }}
                >
                  <Card sx={{ p: 3, textAlign: 'center', borderRadius: 3 }}>
                    <Avatar sx={{ bgcolor: achievement.color + '15', color: achievement.color, margin: '0 auto 16px', width: 60, height: 60 }}>
                      {achievement.icon}
                    </Avatar>
                    <Typography variant="h3" fontWeight="bold" sx={{ color: achievement.color, fontSize: { xs: '1.5rem', md: '2rem' } }}>
                      {achievement.number}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                      {achievement.label}
                    </Typography>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Our Commitment - No Image, Centered */}
      <Box sx={{ py: 10, bgcolor: '#fefefe' }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center' }}
          >
            <Chip 
              label="Our Commitment" 
              sx={{ mb: 2, bgcolor: '#e6010810', color: '#e60108', fontWeight: 600, mx: 'auto', display: 'table' }}
            />
            <Typography variant="h2" sx={{ mb: 3, fontWeight: 800, color: '#191919' }}>
              Building a Sustainable Future
            </Typography>
            <Typography variant="body1" sx={{ color: '#a0a0a0', mb: 2, lineHeight: 1.8, maxWidth: 800, mx: 'auto' }}>
              We believe that global connectivity and environmental responsibility go hand in hand. 
              Our sustainability strategy focuses on reducing our environmental impact while 
              enabling green solutions for our customers.
            </Typography>
            <Typography variant="body1" sx={{ color: '#a0a0a0', mb: 3, lineHeight: 1.8, maxWidth: 800, mx: 'auto' }}>
              From renewable energy-powered data centers to e-waste reduction programs, 
              we're committed to making a positive impact on our planet.
            </Typography>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                variant="contained"
                endIcon={<ArrowForward />}
                onClick={() => navigate('/contact')}
                sx={{
                  bgcolor: '#e60108',
                  px: 4,
                  py: 1.5,
                  borderRadius: 3,
                  mx: 'auto',
                  display: 'inline-flex',
                  '&:hover': { bgcolor: '#b80006', transform: 'translateY(-2px)' }
                }}
              >
                Learn More About Our Initiatives
              </Button>
            </motion.div>
          </motion.div>
        </Container>
      </Box>

      {/* Key Initiatives */}
      <Box sx={{ py: 10, bgcolor: '#f8f8f8' }}>
        <Container maxWidth="xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center' }}
          >
            <Chip
              label="Key Initiatives"
              sx={{ mb: 2, bgcolor: '#e6010810', color: '#e60108', fontWeight: 600, mx: 'auto', display: 'table' }}
            />
            <Typography variant="h2" sx={{ mb: 2, fontWeight: 800, color: '#191919' }}>
              Key Initiatives
            </Typography>
            <Typography variant="h6" sx={{ color: '#a0a0a0', mb: 6, maxWidth: 700, mx: 'auto' }}>
              Our roadmap to a sustainable future
            </Typography>
          </motion.div>

          <Grid container spacing={3} justifyContent="center">
            {initiatives.map((initiative, index) => (
              <Grid item xs={12} md={6} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                >
                  <Card sx={{ p: 3, borderRadius: 3 }}>
                    <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 2 }}>
                      <Avatar sx={{ bgcolor: initiative.color + '15', color: initiative.color }}>
                        {initiative.icon}
                      </Avatar>
                      <Box flex={1}>
                        <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: '#191919' }}>
                          {initiative.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                          {initiative.description}
                        </Typography>
                      </Box>
                    </Stack>
                    <Box sx={{ mt: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="caption" sx={{ color: '#a0a0a0' }}>Progress</Typography>
                        <Typography variant="caption" fontWeight="bold" sx={{ color: initiative.color }}>{initiative.progress}%</Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={initiative.progress} 
                        sx={{ 
                          height: 8, 
                          borderRadius: 4,
                          bgcolor: initiative.color + '20',
                          '& .MuiLinearProgress-bar': { bgcolor: initiative.color }
                        }} 
                      />
                      <Typography variant="caption" sx={{ color: '#a0a0a0', mt: 1, display: 'block' }}>
                        Target: {initiative.target}
                      </Typography>
                    </Box>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Green Technology */}
      <Box sx={{ py: 10, bgcolor: '#fefefe' }}>
        <Container maxWidth="xl">
          <Grid container spacing={6} alignItems="center" direction="row-reverse">
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Box
                  component="img"
                  src="https://images.unsplash.com/photo-1509391366360-2e959784a276?w=600&h=400&fit=crop"
                  alt="Green Technology"
                  sx={{
                    width: '100%',
                    borderRadius: 4,
                    boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                  }}
                />
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Chip 
                  label="Green Technology" 
                  sx={{ mb: 2, bgcolor: '#e6010810', color: '#e60108', fontWeight: 600 }}
                />
                <Typography variant="h3" sx={{ mb: 3, fontWeight: 800, color: '#191919' }}>
                  Innovative Green Solutions
                </Typography>
                <List>
                  {greenTech.map((tech, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <CheckCircle sx={{ color: '#e60108' }} />
                      </ListItemIcon>
                      <ListItemText primary={tech} primaryTypographyProps={{ sx: { color: '#191919' } }} />
                    </ListItem>
                  ))}
                </List>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Community Impact */}
      <Box sx={{ py: 10, bgcolor: '#f8f8f8' }}>
        <Container maxWidth="xl">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Box
                  component="img"
                  src="https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?w=600&h=400&fit=crop"
                  alt="Community Impact"
                  sx={{
                    width: '100%',
                    borderRadius: 4,
                    boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                  }}
                />
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Chip 
                  label="Community Impact" 
                  sx={{ mb: 2, bgcolor: '#e6010810', color: '#e60108', fontWeight: 600 }}
                />
                <Typography variant="h3" sx={{ mb: 3, fontWeight: 800, color: '#191919' }}>
                  Empowering Communities
                </Typography>
                <Typography variant="body1" sx={{ color: '#a0a0a0', mb: 3, lineHeight: 1.8 }}>
                  Beyond environmental sustainability, we're committed to creating positive social 
                  impact in the communities we serve through digital inclusion programs, 
                  educational initiatives, and local partnerships.
                </Typography>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ bgcolor: '#e6010815', color: '#e60108' }}>
                      <Public />
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#191919' }}>
                        Digital Inclusion
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#a0a0a0' }}>
                        Providing internet access to 1M underserved people by 2026
                      </Typography>
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ bgcolor: '#e6010815', color: '#e60108' }}>
                      <School />
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#191919' }}>
                        STEM Education
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#a0a0a0' }}>
                        Supporting technology education in 500+ schools
                      </Typography>
                    </Box>
                  </Stack>
                </Stack>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <CTASection 
        title="Join Our Green Initiative" 
        subtitle="Partner with us for sustainable communication solutions"
        buttonText="Learn More"
        buttonLink="/contact"
      />
    </Box>
  );
};

export default Sustainability;